// OGC-531 Add or rename a renovation type (v19). The internal key is set once
// when the type is created and stays read-only; rename edits the display
// name only. Duplicate names (any type, archived included) are refused here
// and by the server. A new type is appended after the active types.
import { useEffect, useId, useRef, useState } from 'react';
import { notifyCuratedCatalogChanged } from '../../curated-catalog.js';
import CatalogDialog from './CatalogDialog.jsx';
import { attemptKey, catalogErrorMessage, makeUuid, renovationTypeKey, sameName } from './creatives-api.js';

export default function RenovationTypeDialog({ open, type = null, types = [], apiClient, onClose, onSaved, returnFocus = null }) {
  const formId = useId();
  const inputId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const attempt = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(type?.displayName || '');
    setError('');
    setBusy(false);
    attempt.current = null;
  }, [open, type?.id]);

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    const displayName = name.trim().replace(/\s+/g, ' ');
    if (!displayName) {
      setError('Enter a renovation type name.');
      document.getElementById(inputId)?.focus();
      return;
    }
    if (types.some((candidate) => candidate.id !== type?.id && sameName(candidate.displayName, displayName))) {
      setError('A renovation type with this name already exists.');
      document.getElementById(inputId)?.focus();
      return;
    }
    if (type && displayName === type.displayName) {
      onClose?.();
      return;
    }
    // One attempt (ids and idempotency key) per entered name, so a retry
    // after a lost response replays instead of creating a second type.
    if (!attempt.current || attempt.current.name !== displayName) {
      const id = type?.id || makeUuid();
      attempt.current = {
        name: displayName,
        id,
        key: type ? attemptKey('renovation-type-rename', id) : `catalog-renovation-type-${id}`,
        typeKey: type ? null : renovationTypeKey(displayName, types.map((candidate) => candidate.key)),
      };
    }
    setBusy(true);
    setError('');
    try {
      let saved;
      if (type) {
        saved = (await apiClient.renameType(type.id, displayName, attempt.current.key))?.renovationType || { ...type, displayName };
      } else {
        const order = Math.max(0, ...types.filter((candidate) => !candidate.archivedAt).map((candidate) => Number(candidate.displayOrder) || 0)) + 1;
        saved = (await apiClient.createType({
          id: attempt.current.id,
          key: attempt.current.typeKey,
          displayName,
          displayOrder: order,
        }, attempt.current.key))?.renovationType || { id: attempt.current.id, key: attempt.current.typeKey, displayName, displayOrder: order };
      }
      notifyCuratedCatalogChanged();
      setBusy(false);
      onSaved?.(saved, { created: !type });
    } catch (failure) {
      setBusy(false);
      setError(catalogErrorMessage(failure, type ? 'The renovation type could not be renamed. Try again.' : 'The renovation type could not be added. Try again.'));
    }
  }

  return (
    <CatalogDialog
      open={open}
      busy={busy}
      error={error}
      returnFocus={returnFocus}
      initialFocus={`[id="${inputId}"]`}
      title={type ? 'Rename renovation type' : 'Add renovation type'}
      description={type ? 'Update the name shown in the creative library.' : 'Choose a clear name customers will recognize.'}
      onClose={onClose}
      actions={[
        { label: 'Cancel', onClick: onClose },
        { label: type ? 'Save changes' : 'Add type', tone: 'confirm', type: 'submit', form: formId, busyLabel: type ? 'Saving…' : 'Adding…' },
      ]}
    >
      <form id={formId} onSubmit={submit} noValidate>
        <label className="dialog-field" htmlFor={inputId}>
          Name
          <input
            id={inputId}
            value={name}
            maxLength={120}
            placeholder="e.g. Basement"
            autoComplete="off"
            aria-invalid={error ? 'true' : undefined}
            onChange={(event) => { setName(event.target.value); if (error) setError(''); }}
          />
        </label>
        <p className="type-key">{type ? `Key: ${type.key} · Set when created` : 'An internal key is created automatically.'}</p>
      </form>
    </CatalogDialog>
  );
}
